import React, { Component, createContext } from "react";

export interface PermissionType {
  title: string;
  resourceName: string;
  action: string;
  slug: string;
}

export interface VisitorContextType {
  id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  avatarThumbnailUrl?: string;
  geoLocation?: {
    postalCode?: string;
    city?: string;
    countryCode?: string;
    countryName?: string;
    latitude?: number | string;
    longitude?: number | string;
    regionCode?: string;
    regionName?: string;
    timeZone?: string;
    dmaCode?: string;
  };
  permissions?: PermissionType[];
  updateContext: (...args: any[]) => void;
}

const VisitorContext = createContext<VisitorContextType>({
  updateContext: () => {},
});

export const VisitorContextConsumer = VisitorContext.Consumer;

export type VisitorContextProps = {
  value: Pick<
    VisitorContextType,
    | "id"
    | "email"
    | "firstName"
    | "lastName"
    | "avatarThumbnailUrl"
    | "geoLocation"
  >;
};
type State = VisitorContextType;

export class VisitorContextProvider extends Component<
  VisitorContextProps,
  State
> {
  constructor(props: VisitorContextProps) {
    super(props);

    this.state = {
      ...this.buildVisitorContextValue(props.value),
      updateContext: this.updateContext,
    };
  }

  buildVisitorContextValue = (values: VisitorContextProps["value"]) => {
    return {
      ...values,
      fullName: `${values.firstName} ${values.lastName}`,
    };
  };

  updateContext = (values: VisitorContextProps["value"]) => {
    this.setState(this.buildVisitorContextValue(values));
  };

  identifyUser = () => {
    let params: any = {};
    const { email } = this.state;

    if (email) {
      params.email = email;
    }
  };

  componentDidUpdate() {
    this.identifyUser();
  }

  componentDidMount() {
    this.identifyUser();
  }

  render() {
    return (
      <VisitorContext.Provider
        value={this.state}
        children={this.props.children}
      />
    );
  }
}

export function useVisitorContext() {
  return React.useContext(VisitorContext);
}

export const internalEmail = (email: string | undefined) => {
  return (
    email !== undefined &&
    (email.endsWith("floorforce.com") || email.endsWith("broadlume.com"))
  );
};

export const checkPermissions = (
  permissions: PermissionType[] | undefined,
  email: string | undefined,
  resource: string,
  action: string,
  flipper = false
) => {
  if (!flipper) {
    return true;
  }

  if (internalEmail(email)) {
    return resource !== "rsa";
  }

  return (
    permissions !== undefined &&
    permissions.some((p) => p.resourceName === resource && p.action === action)
  );
};

export default VisitorContext;
